import {FACTIONS} from '../constants/factions';
import {CHESTS} from '../constants/chests';
import {TUTORIAL_CHAPTERS} from './tutorial';
import {LEAGUES} from '../constants/leagues';
import {LEAGUE_CONFIGS} from './leagues';

/**
 * Задания игрока (страница «Задания», кнопка в профиле, бейдж в меню).
 *
 * Конфиг общий для клиента и сервера — как configs/tutorial: сервер по нему
 * проверяет условия и начисляет награду (см. finalizeGame и
 * POST /api/users/me/tutorial-chapters/complete), клиент рисует плашки.
 *
 * Награда описывается теми же полями, что у глав обучения (reward +
 * rewardIfFactionOwned), поэтому фактическую награду для игрока считает та же
 * getTutorialChapterReward.
 */

export const QUEST_TYPES = {
  // Прохождение главы обучения: выполненность берется из
  // user.completedTutorialChapters, награду выдает эндпоинт завершения главы
  TUTORIAL_CHAPTER: 'tutorial_chapter',
  // Локальный матч против ботов: выполненность и награду проставляет сервер
  // при сохранении итогов партии (см. POST /api/users/me/local-matches).
  // С него начинается воронка новичка: сначала бой с ботами, потом обычный
  // сетевой матч, потом рейтинг
  LOCAL_MATCH: 'local_match',
  // Сетевой матч: выполненность и награду проставляет сервер при
  // завершении матча (см. finalizeGame)
  ONLINE_MATCH: 'online_match',
  // Сюжетная кампания фракции: пока не реализована — задание заблокировано
  FACTION_CAMPAIGN: 'faction_campaign',
  // Первый рейтинговый матч: как ONLINE_MATCH, но засчитывается рейтинговым
  RANKED_MATCH: 'ranked_match',
  // Подъём по лигам: засчитывается по НОВОМУ рейтингу в конце рейтингового
  // матча — сразу за все лиги, до которых игрок дотянулся
  LEAGUE: 'league',
};

// Лиги, за которые есть задания: от Серебряной до Божественной (Бронзовая —
// стартовая, за неё награждать не за что). Порядок = порядок в списке заданий
export const LEAGUE_QUEST_IDS = [LEAGUES.SILVER, LEAGUES.GOLD, LEAGUES.DIAMOND, LEAGUES.RUBY, LEAGUES.GOD];

// Сундук, который сыплется за лигу: по 10 штук уровня самой лиги
export const LEAGUE_QUEST_CHESTS = {
  [LEAGUES.SILVER]: CHESTS.SILVER,
  [LEAGUES.GOLD]: CHESTS.GOLD,
  [LEAGUES.DIAMOND]: CHESTS.DIAMOND,
  [LEAGUES.RUBY]: CHESTS.RUBY,
  [LEAGUES.GOD]: CHESTS.GOD,
};

export const LEAGUE_QUEST_CHESTS_COUNT = 10;

// «Серебряная лига» -> «в Серебряную лигу» для заголовка задания
const LEAGUE_QUEST_TITLE_BY_ID = {
  [LEAGUES.SILVER]: 'Подняться в Серебряную лигу',
  [LEAGUES.GOLD]: 'Подняться в Золотую лигу',
  [LEAGUES.DIAMOND]: 'Подняться в Алмазную лигу',
  [LEAGUES.RUBY]: 'Подняться в Рубиновую лигу',
  [LEAGUES.GOD]: 'Подняться в Божественную лигу',
};

export const QUESTS = [
  // Главы обучения: по заданию на главу, награды — те же, что у самих глав
  // (их выдача уже реализована завершением главы, дублировать не нужно)
  ...TUTORIAL_CHAPTERS.map((chapter) => ({
    id: `tutorial_${chapter.id}`,
    type: QUEST_TYPES.TUTORIAL_CHAPTER,
    chapterId: chapter.id,
    title: chapter.title,
    description: chapter.description,
    reward: chapter.reward,
    rewardIfFactionOwned: chapter.rewardIfFactionOwned,
  })),

  {
    id: 'local_match_not_last',
    type: QUEST_TYPES.LOCAL_MATCH,
    title: 'Сыграть матч против ботов',
    description: 'Разомнись в локальной партии: соперники — боты, рейтинг не тронется',
    conditions: ['Завершить матч не на последнем месте'],
    reward: {type: 'chest', chestId: CHESTS.RANDOM_EPIC},
  },

  {
    id: 'online_match_not_last',
    type: QUEST_TYPES.ONLINE_MATCH,
    title: 'Сыграть обычный сетевой матч',
    description: 'Сразись с другими игроками в матче без рейтинга',
    // Дополнительные условия — отдельными строками под описанием, со значком
    // восклицательного знака (см. QuestsPage)
    conditions: ['Завершить матч не на последнем месте'],
    reward: {type: 'faction', factionId: FACTIONS.VENTORY},
    rewardIfFactionOwned: {type: 'chest', chestId: CHESTS.RANDOM_EPIC},
  },

  {
    id: 'campaign_humans_chapter_1',
    type: QUEST_TYPES.FACTION_CAMPAIGN,
    factionId: FACTIONS.HUMANS,
    title: 'Пройти первую главу сюжетной кампании Людей',
    description: 'Сюжетная кампания в разработке',
    // Задание еще нельзя выполнить: плашка с замком, по клику — модалка с
    // пояснением (см. QuestsPage)
    isLocked: true,
    reward: {type: 'faction', factionId: FACTIONS.DWARVES},
    rewardIfFactionOwned: {type: 'chest', chestId: CHESTS.RANDOM_EPIC},
  },

  {
    id: 'ranked_match_first',
    type: QUEST_TYPES.RANKED_MATCH,
    title: 'Сыграть первый рейтинговый матч',
    description: 'Проверь себя в матче за рейтинг и лигу',
    reward: {type: 'chest', chestId: CHESTS.RANDOM_EPIC},
  },

  // Задания на лиги: напоминать о них не нужно (см. noReminder) — игрок и так
  // растёт в рейтинге, а «догнать лигу» по требованию нельзя
  ...LEAGUE_QUEST_IDS.map((leagueId) => {
    const league = LEAGUE_CONFIGS.find((config) => config.id === leagueId);
    return {
      id: `league_${leagueId}`,
      type: QUEST_TYPES.LEAGUE,
      leagueId,
      title: LEAGUE_QUEST_TITLE_BY_ID[leagueId],
      description: `Заверши рейтинговый матч с рейтингом ${league.minRating} и выше`,
      reward: {
        type: 'chest',
        chestId: LEAGUE_QUEST_CHESTS[leagueId],
        count: LEAGUE_QUEST_CHESTS_COUNT,
      },
      noReminder: true,
    };
  }),
];

export const getQuestConfig = (questId) => QUESTS.find((quest) => quest.id === questId) || null;

/**
 * Выполнено ли задание конкретным игроком.
 * @param {Object} quest - конфиг задания
 * @param {Object} progress
 * @param {string[]} [progress.completedTutorialChapters]
 * @param {string[]} [progress.completedQuests] - задания, отмеченные сервером
 */
export const checkIfQuestCompleted = (quest, {completedTutorialChapters = [], completedQuests = []} = {}) => {
  if (quest.type === QUEST_TYPES.TUTORIAL_CHAPTER) {
    return completedTutorialChapters.includes(quest.chapterId);
  }
  return completedQuests.includes(quest.id);
};

/**
 * Есть ли у игрока невыполненные ДОСТУПНЫЕ задания — по этому признаку в
 * главном меню зажигается бейдж «Задания» на кнопке профиля. Заблокированные
 * задания (сюжетная кампания) не считаются: выполнить их сейчас нельзя
 */
export const checkIfHasIncompleteQuests = (progress) => QUESTS.some(
  (quest) => !quest.isLocked && !checkIfQuestCompleted(quest, progress)
);

/**
 * Есть ли задания, о которых уместно НАПОМИНАТЬ модалкой в главном меню.
 * Кроме заблокированных не считаются и задания с noReminder: их нельзя
 * «пойти и выполнить» по требованию (подъём по лигам), звать за ними незачем
 */
export const checkIfHasReminderQuests = (progress) => QUESTS.some(
  (quest) => !quest.isLocked && !quest.noReminder && !checkIfQuestCompleted(quest, progress)
);

/**
 * Значок задания в списке: у заданий про рейтинг — иконка лиги (у «первого
 * рейтингового матча» бронзовая, с неё все и начинают), у остальных — общая
 * метка задания. Выполненные и заблокированные рисуются галочкой/замком
 * отдельно, сюда не попадают
 */
export const getQuestIconPath = (quest) => {
  if (quest.type === QUEST_TYPES.LEAGUE) {
    return LEAGUE_CONFIGS.find((config) => config.id === quest.leagueId)?.iconPath || null;
  }
  if (quest.type === QUEST_TYPES.RANKED_MATCH) {
    return LEAGUE_CONFIGS.find((config) => config.id === LEAGUES.BRONZE)?.iconPath || null;
  }
  return null;
};

// Задание на лигу по её id — нужно серверу при начислении награды
export const getLeagueQuest = (leagueId) => QUESTS.find(
  (quest) => quest.type === QUEST_TYPES.LEAGUE && quest.leagueId === leagueId
) || null;

export default QUESTS;
